import React, {useState} from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {isMobileSelector} from "../../../../chatbotSlice";
import {useAppSelector} from "../../../../../app/hooks";
import {ImageCarouselSlideButton} from "./ImageCarouselSlideButton";
import {SliderButtonPosition} from "../../VehicleSliderButton";
import {ImageCounter} from "./ImageCounter";
import {RENDER_DISTANCE} from "../../VehicleSlider";

interface ImageCarouselProps {
    images: string[];
    onImageClick: (e?: any) => void;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, onImageClick }) => {
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
    const isMobile = useAppSelector(isMobileSelector);
    const len = images.length;
    const idx = currentImageIndex;

    let start = 0;
    if (len > 5) {
        start = Math.min(Math.max(idx - 2, 0), len - 5);
    }
    const end = Math.min(start + 4, len - 1);
    const inWindow = (i: number) => i >= start && i <= end;

    const dotCSS = (i: number, isSelected: boolean) => {
        const isLeftEdge  = i === start && start > 0;
        const isRightEdge = i === end   && end   < len - 1;
        const size = isSelected
            ? 6
            : isLeftEdge || isRightEdge
                ? 3
                : 5;
        return {
            width:  size,
            height: size,
            backgroundColor: isSelected ? "#FFC756" : "transparent",
            borderRadius: "100%"
        };
    };

    const [hover, setHover] = useState(false);

    return <div className='w-full relative'
                onTouchStart={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
    >
        {!isMobile && (
            <>
                <div className="absolute top-1/2 left-0 -translate-y-1/2 transform-gpu z-40">
                    <ImageCarouselSlideButton
                        onClick={() => {setCurrentImageIndex(currentImageIndex - 1)}}
                        position={SliderButtonPosition.BUTTON_LEFT}
                        isDisabled={currentImageIndex === 0 || !hover}
                    />
                </div>
                <div className="absolute top-1/2 right-0 -translate-y-1/2 transform-gpu z-40">
                    <ImageCarouselSlideButton
                        onClick={() => {setCurrentImageIndex(currentImageIndex + 1)}}
                        position={SliderButtonPosition.BUTTON_RIGHT}
                        isDisabled={currentImageIndex === images.length - 1 || !hover}
                    />
                </div>
            </>
        )}

        <div className="absolute bottom-2 right-2 transform-gpu z-40">
            <ImageCounter
                currentIndex={currentImageIndex}
                images={images}
            />
        </div>

        <Carousel
            selectedItem={currentImageIndex}
            onChange={setCurrentImageIndex}
            emulateTouch
            swipeScrollTolerance={1}
            showArrows={false}
            showThumbs={false}
            showStatus={false}
            showIndicators={isMobile}
            renderIndicator={(onClick, isSelected, i, label) =>
                !inWindow(i) ? null : (
                    <li
                        key={i}
                        onClick={onClick}
                        onKeyDown={onClick}
                        role="button"
                        tabIndex={0}
                        aria-label={`${label} ${i + 1}`}
                        className="inline-flex items-center justify-center mx-1 cursor-pointer transform-gpu"
                        style={dotCSS(i, isSelected)}
                    >
                        {!isSelected && (
                            <svg width="100%" height="100%" viewBox="0 0 4 4" fill="none">
                                <circle cx="2" cy="2" r="1.5" fill="#DDDDDD" />
                            </svg>
                        )}
                    </li>
                )
            }
            centerMode={false}
            dynamicHeight={false}
        >
            {images.map((src, i) => {
                const distance = Math.abs(currentImageIndex - i);
                const shouldRender = distance <= RENDER_DISTANCE;

                return (
                    <div
                        key={i}
                        className="h-[220px] object-cover cursor-pointer flex items-center"
                        onClick={onImageClick}
                    >
                        {shouldRender ? (
                            <img
                                src={src || "/no-image.jpeg"}
                                alt={`Slide ${i + 1}`}
                                className="max-h-full max-w-full object-contain"
                                draggable={false}
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/no-image.jpeg";
                                }}
                            />
                        ) : (
                            <div className="w-full h-full bg-gray-100" />
                        )}
                    </div>
                );
            })}
        </Carousel>
    </div>
}
